import {Plugins, type Type, Types} from '@packages/systems/wfdl';
import {
  getPluginName,
  type Qualified,
  TypeName,
  getName,
  qualify,
  typeName,
} from '@packages/systems/wfdl-Qualified';
import {TTypeConstructor} from '@packages/systems/wfdl-ast';

// List of all supported collection list props
export type CollectionListSupportedProp = 'Filter' | 'Sort' | 'Curated';

export const COLLECTION_LIST_PROPS: {
  readonly [K in CollectionListSupportedProp]: K;
} = {
  Filter: 'Filter',
  Sort: 'Sort',
  Curated: 'Curated',
} as const;

/**
 * Checks if a given type name is a collection list prop type - it starts with Filter|Sort|Curated and ends with a mongo id.
 * If that's the case - returns the collection id and the prop type. Otherwise returns null.
 */
export function parseCollectionListPropTypeName(
  name: Qualified<TypeName>
): {collectionId: string; propType: CollectionListSupportedProp} | null {
  if (getPluginName(name) === '__SitePlugin') {
    const matches = getName(name).match(
      // matches `Filter <mongo_object_id>`, `Sort <mongo_object_id>`,
      // and `Curated <mongo_object_id>`
      // captures <mongo_object_id> as a named group 'collectionId'
      /^(?<propType>Filter|Sort|Curated) (?<collectionId>[0-9a-fA-F]{24})$/
    );
    if (
      matches?.groups &&
      matches.groups.collectionId &&
      (matches.groups.propType === COLLECTION_LIST_PROPS.Filter ||
        matches.groups.propType === COLLECTION_LIST_PROPS.Sort ||
        matches.groups.propType === COLLECTION_LIST_PROPS.Curated)
    ) {
      return {
        collectionId: matches.groups.collectionId,
        propType: matches.groups.propType,
      };
    }
  }
  return null;
}

/**
 * Checks if a given type is a collection list prop type of a given kind
 * @param propType
 * @param name Type to check
 */
export const isCollectionListPropsTypeName = (
  propType: CollectionListSupportedProp,
  name: Qualified<TypeName>
): boolean => {
  const parsed = parseCollectionListPropTypeName(name);
  return parsed !== null && parsed.propType === propType;
};

/**
 * Checks if a given type is a Filter type created for a collection list prop.
 * @param name Type to check
 */
export const isCollectionListPropsFilterTypeName = (
  name: Qualified<TypeName>
) => isCollectionListPropsTypeName(COLLECTION_LIST_PROPS.Filter, name);

/**
 * Checks if a given type is a Sort type created for a collection list prop.
 * @param name Type to check
 */
export const isCollectionListPropsSortTypeName = (name: Qualified<TypeName>) =>
  isCollectionListPropsTypeName(COLLECTION_LIST_PROPS.Sort, name);

/**
 * Checks if a given type is a Curated type created for a collection list prop.
 * @param name Type to check
 */
export const isCollectionListPropsCuratedTypeName = (
  name: Qualified<TypeName>
) => isCollectionListPropsTypeName(COLLECTION_LIST_PROPS.Curated, name);

/**
 * Creates a type name string for a collection list prop type.
 */
export const getCollectionListPropTypeNameString = (
  propType: CollectionListSupportedProp,
  collectionId: string
) => `${propType} ${collectionId}`;

/**
 * Creates a type name for a collection list prop type.
 */
export const getCollectionListPropTypeName = (
  propType: CollectionListSupportedProp,
  collectionId: string
) =>
  qualify(
    Plugins.name('__SitePlugin'),
    typeName(getCollectionListPropTypeNameString(propType, collectionId))
  );

/**
 * Creates a type for a collection list prop.
 */
export const createCollectionListPropType = (
  propType: CollectionListSupportedProp,
  collectionId: string
): Type => {
  return TTypeConstructor(
    getCollectionListPropTypeName(propType, collectionId)
  );
};

/**
 * Builds a mapping of property IDs to their associated collection IDs for a Filter collection list prop
 * Returns an object where the keys are property IDs and the values are collection IDs in a form of Mongo ID.* Returns an empty object if the data type has no fields.
 * Returns an empty object if the data type has no fields of the given prop type.
 */
export function buildFilterPropsToCollectionIdMap(
  dataType: Type
): PropertyToCollectionIdMap {
  return buildFieldToCollectionIdMap(COLLECTION_LIST_PROPS.Filter, dataType);
}

/**
 * Builds a mapping of property IDs to their associated collection IDs for a Sort collection list prop
 * Returns an object where the keys are property IDs and the values are collection IDs in a form of Mongo ID. * Returns an empty object if the data type has no fields.
 * Returns an empty object if the data type has no fields of the given prop type.
 */
export function buildSortPropsToCollectionIdMap(
  dataType: Type
): PropertyToCollectionIdMap {
  return buildFieldToCollectionIdMap(COLLECTION_LIST_PROPS.Sort, dataType);
}

/**
 * Builds a mapping of property IDs to their associated collection IDs for a Curated collection list prop
 * Returns an object where the keys are property IDs and the values are collection IDs in a form of Mongo ID. * Returns an empty object if the data type has no fields.
 * Returns an empty object if the data type has no fields of the given prop type.
 */
export function buildCuratedPropsToCollectionIdMap(
  dataType: Type
): PropertyToCollectionIdMap {
  return buildFieldToCollectionIdMap(COLLECTION_LIST_PROPS.Curated, dataType);
}

type PropertyToCollectionIdMap = Record<string, string>;

/**
 * Builds a mapping of property IDs to their associated collection IDs for a given collection list prop
 * Returns an object where the keys are property IDs and the values are collection IDs in a form of Mongo ID.
 * Returns an empty object if the data type has no fields of the given prop type.
 */
const buildFieldToCollectionIdMap = (
  propType: CollectionListSupportedProp,
  dataType: Type
): PropertyToCollectionIdMap => {
  // Initialize the mapping object
  const overrideToCollectionMap: PropertyToCollectionIdMap = {};

  // Get all fields from the data type
  const fields = Types.getRecordFields(dataType);
  if (!fields) {
    return overrideToCollectionMap;
  }

  // Process each field to find associated collections
  for (const fieldId in fields) {
    const fieldType = fields[fieldId];
    if (!fieldType) continue;

    // Get the type constructor name
    const typeConstructorName = Types.getTypeConstructor(fieldType);
    if (!typeConstructorName) continue;

    // Extract collection ID from the type name if it represents a collection
    const parsed = parseCollectionListPropTypeName(typeConstructorName);
    if (parsed && parsed.propType === propType) {
      overrideToCollectionMap[fieldId] = parsed.collectionId;
    }
  }

  return overrideToCollectionMap;
};
